import React from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import { Image } from 'expo-image';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { MODEL_TOTAL } from '@/constants/modelInventory.generated';
import { MODEL_BRANDS, MODEL_CDN } from './landingModels';
import { GhostCta, PrimaryCta, Section, useInteractive, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';

const CHAT_BRAND_NAMES = ['GPT', 'Claude', 'Gemini', 'DeepSeek', 'Qwen', 'Kimi', 'GLM', 'Arya'];
const CHAT_BRANDS = MODEL_BRANDS.filter(brand => CHAT_BRAND_NAMES.includes(brand.name));

const CHAT_FEATURES = [
  { title: 'Talk it through.', copy: 'Use voice when it is easier to say it than type it.', href: '/voice' },
  { title: 'Set a routine.', copy: 'Schedule a morning brief or recurring research in your chat.', href: '/tasks/browse' },
  { title: 'Bring your history.', copy: 'Import past conversations from ChatGPT, Claude, or Grok.', href: '/docs/import-chats' },
  { title: 'Control your memory.', copy: 'Choose what carries forward. Edit or delete it whenever you like.', href: '/privacy-policy' },
];

/** An ordinary text link, with the same anchor semantics on every breakpoint. */
export function EditorialLink({ href, children, style }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();
  return (
    <Link href={href} asChild>
      <Pressable {...handlers} accessibilityRole="link" style={{ minHeight: 40, flexDirection: 'row', alignItems: 'center', gap: 10, ...(isWeb ? { cursor: 'pointer' } : {}), ...style }}>
        <Text style={{ flexShrink: 1, color: hovered ? c.accent : c.inkSoft, fontSize: 14, lineHeight: 21, ...(isWeb ? { textDecorationLine: hovered ? 'underline' : 'none' } : {}) }}>{children}</Text>
        <Icon name="arrow-up-right" size={10} color={c.muted} />
      </Pressable>
    </Link>
  );
}

function StorySection({ children, label, background }) {
  const { c, width } = useLanding();
  return (
    <Section label={label} background={background} style={{ borderTopWidth: 1, borderTopColor: c.rule }} contentStyle={{ paddingTop: fluid(width, 48, 76), paddingBottom: fluid(width, 48, 76) }}>
      {children}
    </Section>
  );
}

function SectionTitle({ children, style }) {
  const { c, width } = useLanding();
  const size = fluid(width, 30, 40);
  return <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.12), letterSpacing: -size * 0.035, ...(isWeb ? { whiteSpace: 'pre-line' } : {}), ...style }}>{children}</Text>;
}

export function LandingChatSection() {
  const { c, isXS, isCompact, width } = useLanding();
  return (
    <StorySection label="Chat mode">
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 36 : 64 }}>
        <View style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
          <Text weight="medium" style={{ color: c.accent, fontSize: 15, marginBottom: 18 }}>Chat mode</Text>
          <SectionTitle>{'Choose the model.\nKeep the conversation.'}</SectionTitle>
          <Text style={{ color: c.inkSoft, fontSize: 16, lineHeight: 26, marginTop: 22, maxWidth: 420 }}>Ask a question, attach a file, or search the web. Pick a model yourself, switch between messages, or let Auto choose for you.</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 24, rowGap: 8, marginTop: 26 }}>
            <PrimaryCta restrained label="Start chatting" href="/app" full={isXS} />
            <GhostCta label="Browse all models" href="/models" />
          </View>
        </View>
        <View style={{ flex: isCompact ? undefined : 1.25, minWidth: 0, justifyContent: 'center' }}>
          <Text weight="semibold" style={{ color: c.ink, fontSize: 16, lineHeight: 24, marginBottom: 12 }}>Frontier labs. Open-source favorites. Your choice.</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {CHAT_BRANDS.map(brand => (
              <View key={brand.name} style={{ width: isXS ? '50%' : '25%', minHeight: 76, flexDirection: 'row', alignItems: 'center', gap: 9, borderBottomWidth: 1, borderBottomColor: c.rule, paddingRight: 8 }}>
                <Image source={{ uri: `${MODEL_CDN}${brand.logo}` }} style={{ width: 24, height: 24, borderRadius: 4 }} contentFit="contain" accessibilityLabel={`${brand.name} logo`} />
                <Text weight="medium" style={{ color: c.ink, fontSize: 15, letterSpacing: -0.25 }}>{brand.name}</Text>
              </View>
            ))}
          </View>
          <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 23, marginTop: 22 }}><Text weight="semibold" style={{ color: c.ink, fontSize: 14 }}>{MODEL_TOTAL} models. One account.</Text> Explore the full library for chat, images, video, audio, and more.</Text>
        </View>
      </View>
      <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 24 : 36, marginTop: fluid(width, 36, 48), paddingTop: fluid(width, 28, 36), borderTopWidth: 1, borderTopColor: c.rule }}>
        {CHAT_FEATURES.map(feature => (
          <View key={feature.title} style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
            <EditorialLink href={feature.href}><Text weight="semibold" style={{ color: c.ink, fontSize: 17, lineHeight: 25 }}>{feature.title}</Text></EditorialLink>
            <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, marginTop: 10 }}>{feature.copy}</Text>
          </View>
        ))}
      </View>
    </StorySection>
  );
}
